import React from 'react';
import { useSelector } from 'react-redux';
import {
	Modal,
	ModalContent,
	ModalFooter,
	ModalHeader,
	RegularButton,
	Typography,
} from 'fronton-react';
import { selectIsSmallScreen } from 'store/pageStore';
import {
	ModalButtons,
	modalContentItemStyles,
	ModalDescription,
	ModalInfo,
	ModalInner,
	ModalPhoto,
	modalCloseButtonStyles,
	modalFooterStyles,
	modalMobileTitleStyles,
	modalTitleStyles,
} from './styles';

type ContentItemProps = {
	text: string;
};

type ContentProps = {
	buttonText: string;
	title: string;
	contentItems: ContentItemProps['text'][];
	onClick: VoidFunction;
};

type LargeContentProps = ContentProps & {
	imgUrl: string;
};

type ModalProps = {
	isOpen: boolean;
	onClose: VoidFunction;
	onClick: VoidFunction;
};

type BaseModalProps = ModalProps & LargeContentProps;

const ContentItem = ({ text }: ContentItemProps) => (
	<Typography className={modalContentItemStyles} as="p" size="body_long" variant="m">
		{text}
	</Typography>
);

const SmallContent = ({ contentItems, buttonText, title, onClick }: ContentProps) => (
	<>
		<ModalHeader className={modalTitleStyles} title={title} />
		<div className={modalMobileTitleStyles}>
			<Typography as="span" variant="h2">
				{title}
			</Typography>
		</div>
		<ModalContent>
			{contentItems.map(item => (
				<ContentItem key={item} text={item} />
			))}
		</ModalContent>
		<ModalFooter className={modalFooterStyles}>
			<RegularButton size="l" variant="primary" onClick={onClick}>
				{buttonText}
			</RegularButton>
		</ModalFooter>
	</>
);

const LargeContent = ({ contentItems, buttonText, title, imgUrl, onClick }: LargeContentProps) => (
	<ModalInner>
		<ModalInfo>
			<Typography size="body_accent" variant="l">
				{title}
			</Typography>
			<ModalDescription>
				{contentItems.map(item => (
					<ContentItem key={item} text={item} />
				))}
			</ModalDescription>
			<ModalButtons>
				<RegularButton size="l" variant="primary" onClick={onClick}>
					{buttonText}
				</RegularButton>
			</ModalButtons>
		</ModalInfo>
		<ModalPhoto data-qa="curtains-kind-large-content-image" src={imgUrl} alt={title} />
	</ModalInner>
);

export const BaseModal = ({
	title,
	contentItems,
	buttonText,
	isOpen,
	imgUrl,
	onClose,
	onClick,
}: BaseModalProps) => {
	const isSmallScreen = useSelector(selectIsSmallScreen);
	const handleButtonClick = () => {
		onClose();
		onClick();
	};
	return (
		<>
			{isOpen && (
				<Modal
					className={!isSmallScreen && modalCloseButtonStyles}
					modalID="modal"
					variant="curtain"
					onClose={onClose}
					show={isOpen}
					size={isSmallScreen ? 's' : 'l'}
				>
					{isSmallScreen ? (
						<SmallContent
							contentItems={contentItems}
							buttonText={buttonText}
							title={title}
							onClick={handleButtonClick}
						/>
					) : (
						<LargeContent
							contentItems={contentItems}
							buttonText={buttonText}
							title={title}
							onClick={handleButtonClick}
							imgUrl={imgUrl}
						/>
					)}
				</Modal>
			)}
		</>
	);
};
